import React from 'react';
import { Link } from 'react-router-dom';
import { Wrench, CheckCircle, ArrowRight, Phone, Mail, Clock, Shield, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { CONTACT } from '../lyphline';
import { TurnaroundNote } from '../components/TurnaroundBadge';
import { WorkshopGallery, IllustratedCapabilities } from '../components/WorkshopGallery';
import SEO from '../components/SEO';

const RepairServices = () => {
  const repairServices = [
    {
      title: 'Complete Rebuild',
      description: 'Total freeze dryer rebuild to enhance performance and correct engineering challenges',
      icon: Package
    },
    {
      title: 'Refrigeration Service',
      description: 'Compressor diagnostics, refrigerant recharge, and system rebuilds',
      icon: Wrench
    },
    {
      title: 'Control Board Rebuild',
      description: 'Electronic diagnostics, board replacement, and software updates',
      icon: Shield
    },
    {
      title: 'Preventive Maintenance',
      description: 'Full system inspection, cleaning, and tune up services',
      icon: CheckCircle
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="repair-services-page">
      <SEO title="Freeze Dryer Rebuilds & Repair Services" description="Professional freeze dryer rebuilds, retrofits and repairs for every make and size. Free diagnostic quote, 90-day rebuild warranty, serving Canada and the USA." />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-44 pb-20 bg-gradient-to-br from-[#003456] to-[#002B47] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#CE8946] rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#CE8946] text-[#003456] px-4 py-2 rounded-md text-sm font-semibold mb-6">
              <Wrench className="h-4 w-4" />
              Expert Rebuild Services
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="repair-headline">
              Freeze Dryer
              <span className="block text-[#CE8946]">Rebuild &amp; Modification Services</span>
            </h1>

            <p className="text-xl text-white/80 mb-8 max-w-2xl">
              Professional freeze dryer rebuild and modification services for all major brands.
              We specialize in enhancing performance and addressing engineering challenges to extend the life of your equipment.
            </p>

            <div className="inline-flex items-center gap-1.5 bg-[#CE8946]/20 text-[#CE8946] text-sm font-semibold px-3.5 py-1.5 rounded-full mb-5" data-testid="turnaround-badge">
              <Package className="h-4 w-4" /> Emergency shipping available
            </div>

            <div className="flex flex-wrap gap-4">
              <Link to="/repair/quote">
                <Button className="bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-semibold px-8 py-6 text-lg" data-testid="repair-quote-cta">
                  Request Rebuild Quote <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href={CONTACT.phoneHref}>
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg" data-testid="repair-call-cta">
                  <Phone className="mr-2 h-5 w-5" /> {CONTACT.phone}
                </Button>
              </a>
            </div>
            <TurnaroundNote className="text-white/60 mt-5" />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#003456] mb-4">Our Rebuild Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive rebuild and maintenance services to keep your freeze dryer running at peak performance
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {repairServices.map((service, i) => (
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100">
                <div className="w-14 h-14 bg-[#003456]/5 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-[#CE8946]" />
                </div>
                <h3 className="text-xl font-semibold text-[#003456] mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Illustrated capabilities */}
      <IllustratedCapabilities bg="bg-white" />

      {/* Workshop gallery */}
      <WorkshopGallery bg="bg-[#F1F5F9]" />

      {/* Areas we serve — internal links to location pages */}
      <section className="py-16 section-padding bg-white border-t border-slate-100" data-testid="areas-we-serve">
        <div className="max-w-7xl mx-auto">
          <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Areas we serve</span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#003456] mt-3 mb-6">Freeze dryer repair across Canada &amp; the USA</h2>
          <div className="flex flex-wrap gap-2">
            {[
              ['alberta', 'Alberta'], ['british-columbia', 'British Columbia'], ['saskatchewan', 'Saskatchewan'],
              ['manitoba', 'Manitoba'], ['ontario', 'Ontario'], ['quebec', 'Quebec'], ['united-states', 'the United States'],
              ['calgary', 'Calgary'], ['edmonton', 'Edmonton'], ['vancouver', 'Vancouver'], ['kelowna', 'Kelowna'],
              ['saskatoon', 'Saskatoon'], ['regina', 'Regina'], ['winnipeg', 'Winnipeg'], ['toronto', 'Toronto'],
              ['ottawa', 'Ottawa'], ['montreal', 'Montreal'],
            ].map(([slug, label]) => (
              <Link key={slug} to={`/freeze-dryer-repair/${slug}`} className="text-sm px-4 py-2 rounded-full bg-[#F1F5F9] border border-slate-200 text-[#003456] hover:border-[#CE8946] transition-colors" data-testid={`area-link-${slug}`}>
                Freeze dryer repair in {label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Rebuild CTA Section */}
      <section className="py-20 bg-[#003456]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Professional Rebuild Services
                <span className="block text-[#CE8946]">For All Major Brands</span>
              </h2>
              <p className="text-white/80 mb-8 text-lg">
                We offer complete freeze dryer rebuilds, not quick fixes. Each machine has unique engineering
                challenges, and our team works to correct them. We rebuild to enhance performance, giving your
                equipment new life with improved reliability.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  'Complete system rebuild and enhancement',
                  'Engineering challenge corrections',
                  'Performance enhancement modifications',
                  '90 day rebuild warranty',
                ].map((line) => (
                  <div key={line} className="flex items-center gap-3 text-white">
                    <CheckCircle className="h-5 w-5 text-[#CE8946]" />
                    <span>{line}</span>
                  </div>
                ))}
              </div>

              <Link to="/repair/quote">
                <Button className="bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-semibold px-8 py-6 text-lg">
                  Start Your Free Diagnostic <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="bg-white/10 rounded-2xl p-8 backdrop-blur-sm border border-white/10">
              <h3 className="text-xl font-bold text-white mb-6">Contact Our Rebuild Team</h3>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#CE8946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="h-6 w-6 text-[#003456]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Call Us</p>
                    <a href={CONTACT.phoneHref} className="text-white text-lg font-semibold hover:text-[#CE8946]">
                      {CONTACT.phone}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#CE8946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="h-6 w-6 text-[#003456]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Email Us</p>
                    <a href={`mailto:${CONTACT.email}`} className="text-white text-lg font-semibold hover:text-[#CE8946] break-all">
                      {CONTACT.email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#CE8946] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="h-6 w-6 text-[#003456]" />
                  </div>
                  <div>
                    <p className="text-white/70 text-sm">Business Hours</p>
                    <p className="text-white text-lg font-semibold">{CONTACT.hours}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#003456] text-center mb-12">Rebuild FAQs</h2>

          <div className="space-y-6">
            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
              <h3 className="font-semibold text-[#003456] mb-2">How do I know if my freeze dryer needs a rebuild?</h3>
              <p className="text-gray-600">
                Common signs include unusual noises, longer cycle times, incomplete freeze drying,
                error codes, or visible leaks. If you notice any of these, contact us for a diagnostic assessment.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
              <h3 className="font-semibold text-[#003456] mb-2">Do you rebuild all freeze dryer brands?</h3>
              <p className="text-gray-600">
                Yes. Our technicians can rebuild and modify select makes and models of freeze dryers. We can work on
                equipment from any manufacturer and address most common issues through our rebuild process.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
              <h3 className="font-semibold text-[#003456] mb-2">What is the typical rebuild turnaround time?</h3>
              <p className="text-gray-600">
                Turnaround time is calculated after the customer approves the rebuild plan. Once approved,
                most rebuilds are completed within 5 to 10 business days. Rush service is available for an additional fee.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
              <h3 className="font-semibold text-[#003456] mb-2">What's the difference between a rebuild and a modification?</h3>
              <p className="text-gray-600">
                A rebuild restores your machine to reliable working order using quality aftermarket parts. A
                modification goes further, upgrading controls, refrigeration or components to improve performance.
                We can enhance how your machine runs, but we cannot reengineer its fundamental design.
              </p>
            </div>

            <div className="bg-[#F8FAFC] rounded-xl p-6 border border-slate-100">
              <h3 className="font-semibold text-[#003456] mb-2">How do I get my machine to you?</h3>
              <p className="text-gray-600">
                We're a rebuild shop serving all of Canada &amp; the USA.
                Contact us and we'll walk you through packing and shipping your machine in.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/repair/quote">
              <Button className="bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-semibold px-8 py-6 text-lg">
                Get Your Free Diagnostic Quote <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RepairServices;
